import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ResultsView from "@/components/results/ResultsView";
import ConversionModal from "@/components/ConversionModal";
import ChatLauncher from "@/components/chat/ChatLauncher";
import ChatDrawer from "@/components/chat/ChatDrawer";
import { useChat } from "@/context/ChatContext";
import { useLang } from "@/i18n/LanguageContext";
import { loadAnalysis } from "@/lib/persistence";
import { linkAnalysisIfNeeded } from "@/lib/sentinelEmailApi";
import { exportAnalysisToPdf } from "@/lib/pdfExport";
import { toast } from "sonner";

const SOFT_GATE_ENABLED =
  String(process.env.REACT_APP_SOFT_GATE_ENABLED || "true") === "true";
const SOFT_GATE_DELAY_MS = Number(process.env.REACT_APP_SOFT_GATE_DELAY_MS || 18000);

export default function Result() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [result, setResult] = useState(null);
  const [conversionOpen, setConversionOpen] = useState(false);
  const conversionShownRef = useRef(false);
  const linkAttemptedRef = useRef(false);
  const { setAnalysis: setChatAnalysis } = useChat();

  useEffect(() => {
    const saved = loadAnalysis();
    if (!saved?.result) {
      navigate("/", { replace: true });
      return;
    }
    setResult(saved.result);
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "auto" });
    });
  }, [navigate]);

  useEffect(() => {
    setChatAnalysis(result || null);
  }, [result, setChatAnalysis]);

  useEffect(() => {
    if (!result || linkAttemptedRef.current) return;
    linkAttemptedRef.current = true;
    linkAnalysisIfNeeded(result, lang).catch(() => {});
  }, [result, lang]);

  useEffect(() => {
    if (!result) return;
    if (!SOFT_GATE_ENABLED) return;
    if (conversionShownRef.current) return;
    const id = setTimeout(() => {
      conversionShownRef.current = true;
      setConversionOpen(true);
    }, SOFT_GATE_DELAY_MS);
    return () => clearTimeout(id);
  }, [result]);

  const handleReset = useCallback(() => {
    conversionShownRef.current = false;
    navigate("/#hero-upload");
  }, [navigate]);

  const focusHeroUpload = useCallback(() => {
    navigate("/#hero-upload");
  }, [navigate]);

  const handleDownload = useCallback(async () => {
    if (!result) return;
    try {
      await exportAnalysisToPdf(result, t);
      toast.success(t.results.downloadSuccess || "PDF generado");
    } catch (e) {
      toast.error("PDF error");
    }
  }, [result, t]);

  if (!result) {
    return null;
  }

  return (
    <div className="App relative min-h-screen">
      <Header onUpload={focusHeroUpload} onDownload={handleDownload} />

      <div className="pt-[68px]">
        <ResultsView
          result={result}
          onReset={handleReset}
          onUnlock={() => setConversionOpen(true)}
          onDownload={handleDownload}
        />
      </div>

      <ConversionModal open={conversionOpen} onClose={() => setConversionOpen(false)} />

      <ChatLauncher />
      <ChatDrawer />

      <Footer />
    </div>
  );
}
